import { useCallback, useState } from "react";
import { outOfDatePublicationDismissed } from "@/utils/outOfDatePublicationDismissed";

export interface UseOutOfDateDismissalArgs {
  projectId: string;
  /** Must resolve to whether the action actually succeeded — a swallowed failure must resolve `false`, not throw. */
  onRegenerate: () => Promise<boolean>;
  /** Must resolve to whether the action actually succeeded — a swallowed failure must resolve `false`, not throw. */
  onUpdate: () => Promise<boolean>;
}

/**
 * Dismissal bookkeeping for the floating out-of-date publication widget,
 * keyed by project id (see MED-6969) — once dismissed, the page renders
 * `OutOfDatePublicationCompactButton` in its place. Shared by ExperiencePage
 * and ProjectPageNew so the two pages can't drift.
 */
export const useOutOfDateDismissal = ({
  projectId,
  onRegenerate,
  onUpdate,
}: UseOutOfDateDismissalArgs) => {
  // Re-derive during render when projectId changes instead of an effect,
  // so navigating between projects (no remount — same route, param-only
  // change) doesn't carry over the previous project's dismissal state.
  const [seenProjectId, setSeenProjectId] = useState(projectId);
  const [isDismissed, setIsDismissed] = useState(() =>
    outOfDatePublicationDismissed.isDismissed(projectId)
  );
  if (projectId !== seenProjectId) {
    setSeenProjectId(projectId);
    setIsDismissed(outOfDatePublicationDismissed.isDismissed(projectId));
  }

  const dismiss = useCallback(() => {
    outOfDatePublicationDismissed.setDismissed(projectId);
    setIsDismissed(true);
  }, [projectId]);

  const clearDismissal = useCallback(() => {
    outOfDatePublicationDismissed.clear(projectId);
    setIsDismissed(false);
  }, [projectId]);

  // Also clears this project's dismissal, whichever of the widget/compact
  // button triggered it — a no-op if it wasn't dismissed to begin with, and
  // otherwise ensures a later staleness surfaces the floating widget again
  // instead of staying compact. onRegenerate/onUpdate swallow their own
  // errors (quota handling, toasts) instead of throwing, so we gate on their
  // reported success rather than on the await not throwing — otherwise a
  // failed call would clear the dismissal as if it had gone through.
  const handleRegenerate = useCallback(async () => {
    const success = await onRegenerate();
    if (success) clearDismissal();
  }, [onRegenerate, clearDismissal]);

  const handleUpdate = useCallback(async () => {
    const success = await onUpdate();
    if (success) clearDismissal();
  }, [onUpdate, clearDismissal]);

  return { isDismissed, dismiss, handleRegenerate, handleUpdate };
};
